<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('REVERSAL_POSTING')"
            :label="$t('Post')"
            size="md"
            class="mt-0"
            @click="goToSingleTransaction"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="cashDepositList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(transactionDate)="data">
        {{ toDateWithTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer"
          @click="onViewCashDeposit(data.item)"
        />
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="$t('Cancel')"
      ok-only
      :title="$t('Single Transaction')"
      ok-variant="outline-secondary"
      centered
      size="lg"
    >
      <div class="text-center">
        <h3> {{ $t('Posting Reference:') }} {{ reverseTransactionData.postingReferenceNumber }}</h3>
      </div>

      <TableEntries
        v-model="transactionPerPage"
        :options="perPageOptions"
      />

      <b-table
        ref="reverseTransactionRef"
        :items="transactionList"
        responsive
        :busy="isBusy"
        :per-page="transactionPageSize"
        :current-page="transactionCurrentPage"
        :fields="translatedTransactionTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative mt-3"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>

      </b-table>

      <TablePagination
        v-model="transactionCurrentPage"
        :from="transactionFrom"
        :to="transactionTo"
        :total-rows="transactionTotalEntries"
        :per-page="transactionPerPage"
      />
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchReversalList"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!--Posting Reference Number -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.postingReferenceNumber"
            :label="$t('Reference Number')"
            :placeholder="$t('Enter posting reference number')"
            type="text"
          />
        </b-col>
        <!-- Posting Reference Number -->

        <!-- Instrument Number -->
        <b-col md="6">
          <InputField
            id="instrument-number"
            v-model="filter.instrumentNumber"
            :label="$t('Instrument Number')"
            :placeholder="$t('Enter instrument number')"
            type="text"
            for="instrument-number"
          />
        </b-col>
        <!-- Instrument Number -->

        <!-- Entry Number -->
        <b-col md="6">
          <InputField
            id="entry-number"
            v-model="filter.entryCode"
            :label="$t('Entry Number')"
            :placeholder="$t('Enter entry number')"
            type="number"
          />
        </b-col>
        <!-- Entry Number -->

        <!-- GL code -->
        <b-col md="6">
          <InputField
            id="gl-code"
            v-model="filter.glCode"
            :label="$t('GL Code')"
            :placeholder="$t('Enter gl code')"
            type="text"
          />
        </b-col>
        <!-- GL code -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
          <!-- Branch -->
        </b-col>

        <!-- Financial Date From -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateFrom"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date From-->

        <!-- Financial Date To -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateTo"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date To-->

        <!-- Start Date -->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :label="$t('Start Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Start Date -->

        <!-- End Date -->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :label="$t('End Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- End Date -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toBalance } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithTime } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    DatePickerField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        // { key: 'depositor', sortable: true },
        { key: 'initiatorID', sortable: true, label: 'Initiator ID' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'approvedBy', sortable: true, label: 'Approver' },
        { key: 'actions', label: 'Actions' },
      ],
      transactionTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
      ],
      categoryFormData: {
        categoryName: '',
        level: 'Select',
        specifyLevel: 'Select',
      },
      cashDepositList: [],
      reverseTransactionData: {
        accountNumber: '',
        accountName: '',
        branch: '',
        financialDate: '',
        transactionDate: '',
        postingReferenceNumber: '',
        credit: '',
        entryCode: '',
        instrumentNumber: '',
        depositor: '',
        narration: '',
        approver: '',
      },
      filter: {
        accountName: null,
        accountNumber: null,
        postingReferenceNumber: null,
        instrumentNumber: null,
        branchCode: null,
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        entryCode: null,
        glCode: null,
        postingType: 'ReversalPosting',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      transactionList: [],
      transactionPerPage: 10,
      transactionPageSize: 0,
      transactionCurrentPage: 1,
      transactionTotalCount: 0,
      showSpinner: false,
      postingReferenceNumber: null,
    }
  },
  computed: {
    ...mapGetters({
      tillAccount: 'operations/getTillAccount',
      branchOptions: 'operations/branchOptions',
    }),
    transactionTotalEntries() {
      return this.transactionTotalCount
    },
    transactionNumberOfPages() {
      return Math.floor((this.transactionTotalEntries + this.transactionPerPage - 1) / this.transactionPerPage)
    },
    transactionFrom() {
      if (this.transactionTotalCount === 0) {
        return 0
      }
      return (this.transactionCurrentPage * this.transactionPerPage) - (this.transactionPerPage - 1)
    },
    transactionTo() {
      return Math.min(this.transactionFrom + this.transactionPerPage - 1, this.transactionTotalEntries)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionTableHeaders() {
      return this.transactionTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchReversalList()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchReversalList()
    },
    transactionCurrentPage(newValue) {
      this.transactionCurrentPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
    transactionPerPage(newValue) {
      this.transactionPerPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
  },
  mounted() {
    this.fetchReversalList()
    // this.fetchBranches()
  },
  methods: {
    ...mapActions({
      getPostingsReport: 'reports/getTransactionReports',
    }),
    goToSingleTransaction() {
      this.$router.push('cancel-posting')
    },
    async fetchReversalList() {
      try {
        this.isBusy = true
        const response = await this.getPostingsReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          route: 'postingsReport',
          permission: 'VIEW_REVERSAL_POSTING',
        })
        const { data } = await response
        if (data.data !== null) {
          this.isBusy = false
          this.cashDepositList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchPostings(postingReferenceNumber) {
      const response = await this.getPostingsReport({
        search: { postingReferenceNumber },
        page: this.transactionCurrentPage,
        limit: this.transactionPerPage,
        route: 'postingsReport',
        permission: 'VIEW_REVERSAL_POSTING',
      })
      const { data, status } = response
      if (status === 200 && data.data) {
        this.transactionList = data.data.map(item => ({
          ...item,
          credit: toBalance(item.credit),
          debit: toBalance(item.debit),
        }))
        this.transactionTotalCount = data.recordCount
      }
    },
    async onViewCashDeposit(items) {
      try {
        this.showSpinner = true
        this.postingReferenceNumber = items.postingReferenceNumber
        await this.fetchPostings(items.postingReferenceNumber)

        this.reverseTransactionData = {
          ...items,
          transactionDate: this.toDateWithYear(items.transactionDate),
          financialDate: this.toDateWithYear(items.financialDate),
          credit: toBalance(items.credit),
        }
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    resetFilters() {
      this.filter = {
        ...null,
        postingType: 'ReversalPosting',
      }
    },
    resetFilter() {
      this.filter = {
        ...null,
        postingType: 'ReversalPosting',
      }
      this.fetchReversalList()
    },
  },
}
</script>
